/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 18th 2021, 12:15:22 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Switch from "@mui/material/Switch";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import { ActionDialog } from "ui-widgets/dialogs/GenericDialogs";
import { BackdropLoader } from "ui-widgets";
import { useDidMount } from "lib/utils";
import { ActionSnackbar } from "ui-widgets/alerts/Alert";
import { useGlobalScope } from "core/providers";
import { useAPIOrganizationToggleActivation } from "organizations/services/OrganizationsAPI";
import { Organization, OrganizationListItem } from "lib/schemas";

const label = { inputProps: { "aria-label": "Switch organization" } };

interface OrganizationStatusChangeProps {
  item: OrganizationListItem | Organization;
  children?: ({ onClick }: { onClick: () => void }) => React.ReactNode;
  onChangeOrganizationStatus?: (organization: Organization) => void;
}

export const OrganizationStatusChange = (props: OrganizationStatusChangeProps) => {
  const {
    warningDialogOpenState,
    closeWarningDialog,
    updateActiveStatus,
    organization_name,
    organization_status,
    closeSuccessSnack,
    successSnackOpenState,
    openSuccessSnack,
    isMakingAPIRequest,
    openWarningDialog,
    confirmationTitle,
    confirmationDescription,
    confirmationHeading,
  } = useOrganizationStatusChange(props);

  const { children } = props;

  return (
    <React.Fragment>
      <ActionDialog
        severity="warning"
        title={confirmationTitle}
        open={warningDialogOpenState}
        onClose={closeWarningDialog}
        confirmationAction={updateActiveStatus}
        dialogMessage={
          <>
            <p className="lead">{confirmationHeading}</p>
            <p className="lead-support">{confirmationDescription}</p>
          </>
        }
      />
      <ActionSnackbar
        message={`Successfully ${organization_status === "active" ? "activated" : "deactivated"} ${organization_name}.`}
        actionText="Got it"
        actionFunction={closeSuccessSnack}
        open={successSnackOpenState}
        handleClose={closeSuccessSnack}
        handleOpen={openSuccessSnack}
      />
      <BackdropLoader open={isMakingAPIRequest} />
      {children?.({ onClick: openWarningDialog }) ?? (
        <Switch
          {...label}
          checked={organization_status === "active"}
          size="small"
          onClick={() => openWarningDialog()}
        />
      )}
    </React.Fragment>
  );
};

const useOrganizationStatusChange = ({
  item: passed_organization,
  onChangeOrganizationStatus,
}: OrganizationStatusChangeProps) => {
  const [organization_name, setOrganizationName] = React.useState<OrganizationListItem["name"]>(
    passed_organization.name
  );
  const [organization_status, setOrganizationStatus] = React.useState<OrganizationListItem["organization_status"]>(
    passed_organization.organization_status
  );

  let confirmationTitle: string;
  let confirmationHeading: string;
  let confirmationDescription: string;

  switch (organization_status) {
    case "active":
      confirmationTitle = `Deactivate ${organization_name}`;
      confirmationHeading = `Deactivation will make all of ${organization_name}'s Portals and Services inaccessible. Are you sure about
      this?`;
      confirmationDescription = `You will still get billed for deactivated organizations as we still store your data and configurations. To
      stop being billed for an organization account, kindly cancel your subscription in the billing menu. We will
      stop billing you and delete all your data after you cancel a subscription.`;
      break;

    case "deactivated":
      confirmationTitle = `Activate ${organization_name}`;
      confirmationHeading = `Are you sure you want to re-activate ${organization_name}?`;
      confirmationDescription = `Reactivating ${organization_name} will make all of its deployed portals & services 
      accessible to your staff and clients. You can still deactivate this organization at anytime as long as you have
      a valid subscription plan.`;
      break;
  }

  const { patchData, isMakingAPIRequest } = useAPIOrganizationToggleActivation({});

  const globalScope = useGlobalScope();

  const {
    handleClose: closeSuccessSnack,
    open: successSnackOpenState,
    handleOpen: openSuccessSnack,
  } = useToggleState();

  const {
    handleClose: closeWarningDialog,
    open: warningDialogOpenState,
    handleOpen: openWarningDialog,
  } = useToggleState();

  const didMount = useDidMount();

  const updateActiveStatus = () => {
    patchData({
      apiPathParams: [passed_organization.id],
      body: JSON.stringify({ active_status: !(organization_status === "active") }),
      setValidAPIResponse: (apiResponse) => {
        setOrganizationName(apiResponse.name);
        setOrganizationStatus(apiResponse.organization_status);
        onChangeOrganizationStatus?.(apiResponse);
        if (globalScope.globalState.selectedOrganization?.id === apiResponse?.id) {
          globalScope.updateGlobalOrganizationScope({ selectedOrganization: apiResponse });
        }
      },
    });
    closeWarningDialog();
  };

  React.useEffect(() => {
    if (didMount) {
      if (
        !(organization_status === "active") &&
        globalScope.globalState.selectedOrganization?.id === passed_organization?.id
      ) {
        globalScope.updateGlobalOrganizationScope({ selectedOrganization: null });
      }
      openSuccessSnack();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization_status]);

  return {
    warningDialogOpenState,
    openWarningDialog,
    closeWarningDialog,
    updateActiveStatus,
    organization_name,
    organization_status,
    closeSuccessSnack,
    successSnackOpenState,
    openSuccessSnack,
    isMakingAPIRequest,
    confirmationTitle,
    confirmationHeading,
    confirmationDescription,
  };
};
